import React from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest, silentRequest } from "./config/authConfig";
import ContactCard from "./components/ContactCard";
import ViuLogo from "./components/ViuLogo";
import "./assets/styles.css";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Global flag to prevent multiple concurrent login redirects
let isRedirecting = false;

// Debug function to check localStorage state
const debugAuthState = () => {
  console.log("=== Auth State Debug ===");
  console.log("User Agent:", navigator.userAgent);
  console.log("LocalStorage available:", typeof Storage !== "undefined");

  // Check all localStorage entries
  console.log("All localStorage keys:", Object.keys(localStorage));

  // Check MSAL cache
  const msalKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith("msal.")
  );
  console.log("MSAL keys in localStorage:", msalKeys);

  // Print actual MSAL data
  msalKeys.forEach((key) => {
    const value = localStorage.getItem(key);
    console.log(`${key}:`, value);
    // Parse token keys to see if they're empty
    if (key.includes("token.keys")) {
      try {
        const parsed = JSON.parse(value);
        console.log(`  - Parsed token keys:`, parsed);
        console.log(`  - ID tokens count: ${parsed.idToken?.length || 0}`);
        console.log(
          `  - Access tokens count: ${parsed.accessToken?.length || 0}`
        );
        console.log(
          `  - Refresh tokens count: ${parsed.refreshToken?.length || 0}`
        );
      } catch (e) {
        console.log(`  - Could not parse token keys: ${e.message}`);
      }
    }
  });

  // Only check accounts if MSAL is initialized
  try {
    const accounts = msalInstance.getAllAccounts();
    console.log("MSAL accounts:", accounts);

    const activeAccount = msalInstance.getActiveAccount();
    console.log("Active account:", activeAccount);
  } catch (error) {
    console.log("MSAL not ready yet:", error.message);
  }

  // Check MSAL cache configuration
  console.log("MSAL cache config:", msalInstance.getConfiguration().cache);

  console.log("=== End Auth State Debug ===");
};

function App() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [authState, setAuthState] = React.useState(null);

  // Function to trigger automatic login redirect
  const triggerAutoLogin = React.useCallback(() => {
    // Prevent multiple concurrent redirects
    if (isRedirecting) {
      console.log("Login redirect already in progress, skipping...");
      return;
    }

    console.log("Triggering automatic login redirect...");
    isRedirecting = true;
    setIsLoading(true);

    // Small delay to ensure state is set before redirect
    setTimeout(() => {
      msalInstance.loginRedirect(loginRequest).catch((error) => {
        console.error("Login redirect error:", error);
        isRedirecting = false;
      });
    }, 100);
  }, []);

  React.useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Properly await MSAL initialization
        await msalInstance.initialize();
        console.log("MSAL initialized successfully");

        // Debug auth state after initialization
        debugAuthState();

        // Validate and clean up corrupted cache before proceeding
        const msalKeys = Object.keys(localStorage).filter((key) =>
          key.startsWith("msal.")
        );

        // Check if we have token keys but they're empty
        let hasCorruptedCache = false;
        msalKeys.forEach((key) => {
          if (key.includes("token.keys")) {
            try {
              const parsed = JSON.parse(localStorage.getItem(key));
              if (
                parsed.idToken?.length === 0 &&
                parsed.accessToken?.length === 0 &&
                parsed.refreshToken?.length === 0
              ) {
                console.log("Found corrupted token cache with empty arrays");
                hasCorruptedCache = true;
              }
            } catch (e) {
              console.log("Found unparseable token cache");
              hasCorruptedCache = true;
            }
          }
        });

        // Check if we have account keys but they're empty
        const accountKeys = localStorage.getItem("msal.account.keys");
        if (accountKeys) {
          try {
            const parsed = JSON.parse(accountKeys);
            if (Array.isArray(parsed) && parsed.length === 0) {
              console.log("Found empty account keys array");
              hasCorruptedCache = true;
            }
          } catch (e) {
            console.log("Found unparseable account keys");
            hasCorruptedCache = true;
          }
        }

        // If we have corrupted cache, clear it before proceeding
        if (hasCorruptedCache) {
          console.log("Clearing corrupted MSAL cache before proceeding...");
          msalKeys.forEach((key) => {
            localStorage.removeItem(key);
          });
          console.log("Corrupted cache cleared");
        }

        // Always call handleRedirectPromise to handle any pending authentication
        // This is necessary to handle the redirect from Microsoft login
        console.log(
          "Calling handleRedirectPromise to handle any pending authentication..."
        );
        let response = null;
        try {
          response = await msalInstance.handleRedirectPromise();
          console.log("Redirect response:", response);
        } catch (error) {
          console.log("Error handling redirect promise:", error);
          // Continue with normal flow even if redirect handling fails
        }

        if (response) {
          // If user just logged in, set the active account
          console.log("Setting active account from redirect response");
          msalInstance.setActiveAccount(response.account);
          setAuthState("authenticated");
          setIsLoading(false);
        } else {
          // If no redirect response, try to set active account from existing accounts
          const accounts = msalInstance.getAllAccounts();
          console.log("Found accounts in storage:", accounts.length);

          if (accounts.length > 0) {
            console.log("Setting active account from stored accounts");
            msalInstance.setActiveAccount(accounts[0]);

            // Try to silently acquire a token to verify the account is still valid
            try {
              console.log("Attempting silent token acquisition...");
              const silentResponse = await msalInstance.acquireTokenSilent({
                ...silentRequest,
                account: accounts[0],
              });
              console.log(
                "Silent token acquisition successful - user is authenticated"
              );
              console.log("Silent response:", silentResponse);

              // Double check that we actually have a valid token
              if (silentResponse && silentResponse.accessToken) {
                setAuthState("authenticated");
                setIsLoading(false);
              } else {
                console.log("Silent response did not contain valid token");
                // Auto-redirect to login
                triggerAutoLogin();
              }
            } catch (error) {
              console.log("Silent token acquisition failed:", error);
              // If silent acquisition fails, automatically redirect to login
              if (
                error.name === "InteractionRequiredAuthError" ||
                error.name === "BrowserAuthError" ||
                error.message.includes("No cached tokens found")
              ) {
                console.log(
                  "Clearing invalid account state - auto-redirecting to login"
                );
                // Clear the account and all cached data
                msalInstance.setActiveAccount(null);

                // Clear localStorage cache if it's corrupted
                const msalKeys = Object.keys(localStorage).filter((key) =>
                  key.startsWith("msal.")
                );
                msalKeys.forEach((key) => {
                  localStorage.removeItem(key);
                });
                console.log("Cleared corrupted MSAL cache");
                // Auto-redirect to login
                triggerAutoLogin();
              } else {
                // For other errors, also auto-redirect
                triggerAutoLogin();
              }
            }
          } else {
            console.log("No accounts found in storage");

            // Even with no accounts, we might still have refresh tokens
            // Try to acquire a token silently without specifying an account
            // This can work if there are cached refresh tokens
            try {
              console.log(
                "Attempting silent token acquisition without account (using cached refresh tokens)..."
              );
              const silentResponse = await msalInstance.acquireTokenSilent({
                ...silentRequest,
                // Don't specify account - let MSAL try to use any available refresh tokens
              });
              console.log(
                "Silent token acquisition successful with refresh tokens"
              );
              console.log("Silent response:", silentResponse);

              if (
                silentResponse &&
                silentResponse.accessToken &&
                silentResponse.account
              ) {
                // We got a token and account back, set it as active
                msalInstance.setActiveAccount(silentResponse.account);
                setAuthState("authenticated");
                setIsLoading(false);
              } else {
                console.log(
                  "Silent response did not contain valid token or account"
                );
                // Auto-redirect to login
                triggerAutoLogin();
              }
            } catch (error) {
              console.log(
                "Silent token acquisition without account failed:",
                error
              );
              // Auto-redirect to login
              triggerAutoLogin();
            }
          }
        }

        // Debug auth state after processing
        setTimeout(() => debugAuthState(), 100);
      } catch (error) {
        console.error("Error initializing MSAL:", error);
        // Auto-redirect to login on any error
        triggerAutoLogin();
      }
    };

    initializeMsal();
  }, [triggerAutoLogin]);

  // Set up periodic token refresh check
  React.useEffect(() => {
    if (authState === "authenticated") {
      console.log("Setting up periodic token refresh check...");
      const tokenRefreshInterval = setInterval(async () => {
        try {
          const activeAccount = msalInstance.getActiveAccount();
          if (activeAccount) {
            console.log("Checking token freshness...");
            await msalInstance.acquireTokenSilent({
              ...silentRequest,
              account: activeAccount,
            });
            console.log("Token refresh successful");
          }
        } catch (error) {
          console.log("Token refresh failed:", error);
          if (error.name === "InteractionRequiredAuthError") {
            console.log("User needs to re-authenticate - auto-redirecting");
            // Auto-redirect to login instead of showing login screen
            triggerAutoLogin();
          }
        }
      }, 30 * 60 * 1000); // Check every 30 minutes

      return () => {
        console.log("Clearing token refresh interval");
        clearInterval(tokenRefreshInterval);
      };
    }
  }, [authState, triggerAutoLogin]);

  // Handle visibility change to refresh tokens when user returns to tab
  React.useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "visible" &&
        authState === "authenticated"
      ) {
        console.log("Tab became visible, checking token freshness...");
        try {
          const activeAccount = msalInstance.getActiveAccount();
          if (activeAccount) {
            await msalInstance.acquireTokenSilent({
              ...silentRequest,
              account: activeAccount,
            });
            console.log("Token refresh on visibility change successful");
          }
        } catch (error) {
          console.log("Token refresh on visibility change failed:", error);
          if (error.name === "InteractionRequiredAuthError") {
            console.log(
              "User needs to re-authenticate after returning to tab - auto-redirecting"
            );
            // Auto-redirect to login instead of showing login screen
            triggerAutoLogin();
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authState, triggerAutoLogin]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedContent onAuthError={triggerAutoLogin} />
    </MsalProvider>
  );
}

const AuthenticatedContent = ({ onAuthError }) => {
  const { instance } = useMsal();

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  return (
    <div className="app-container">
      <header className="header">
        <ViuLogo className="logo" />
        <button className="login-button" onClick={handleLogout}>
          Logout
        </button>
      </header>
      <main className="main-content">
        <ContactCard onAuthError={onAuthError} />
      </main>
      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} viu AG</p>
      </footer>
    </div>
  );
};

export default App;
